import { emptyProgress } from '@engineer-os/engine';
// The tab's merge and the tab's record, not the engine's. The engine knows
// four fields and rebuilds the record from them, so merging through it drops
// the struggle memory on every pull — silently, because the result is a
// perfectly valid record that has simply forgotten what the learner found
// hard (web/src/progress.ts, `Progress.struggle`). This is the path that runs
// on every sync, so it is the one that has to know.
import { mergeProgress, type Progress } from '../progress';
import { pullProgress, pushProgress, type RemoteProgress } from './account';

export type SyncState = 'off' | 'syncing' | 'synced' | 'error';
interface Ports {
  read(): Progress;
  write(value: Progress, replaced: boolean): void;
  status(value: SyncState): void;
}
interface Session {
  userId: string;
  controller: AbortController;
  epoch?: number;
  pull: boolean;
  dirty: boolean;
  running: boolean;
}
const MARKER = 'engineeros.progress.sync.v1';
function marker(): { userId: string; epoch: number } | null {
  try {
    const value = JSON.parse(localStorage.getItem(MARKER) ?? 'null');
    return typeof value?.userId === 'string' && Number.isSafeInteger(value.epoch) ? value : null;
  } catch { return null; }
}

/** One serial writer per mounted player; D1 handles concurrency across devices. */
export class ProgressSynchronizer {
  private owner = marker();
  private session: Session | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(private readonly ports: Ports) {}

  connect(userId: string | null): void {
    this.disconnect();
    if (!userId) { this.ports.status('off'); return; }
    const previous = this.owner;
    const session: Session = {
      userId, controller: new AbortController(),
      epoch: previous?.userId === userId ? previous.epoch : undefined,
      pull: true, dirty: false, running: false,
    };
    this.session = session;
    void this.drain(session);
  }

  disconnect(): void {
    this.session?.controller.abort();
    this.session = null;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
  }

  changed(): void {
    const session = this.session;
    if (!session) return;
    session.dirty = true;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.timer = null; void this.drain(session); }, 800);
  }

  reconcile(): void {
    if (!this.session) return;
    this.session.pull = true;
    void this.drain(this.session);
  }

  private live(session: Session): boolean { return this.session === session && !session.controller.signal.aborted; }

  /* A remote epoch this device has not seen replaces what it holds rather
     than merging with it. Nothing here advances an epoch any more — the
     browser has no reset — but a build from before that did, on another
     device, and its empty record must not be merged back into. */
  private accept(session: Session, remote: RemoteProgress): void {
    const previous = this.owner;
    const replace = session.epoch !== undefined
      ? session.epoch !== remote.epoch
      : (previous !== null || remote.epoch !== 0);
    session.epoch = remote.epoch;
    this.owner = { userId: session.userId, epoch: remote.epoch };
    try { localStorage.setItem(MARKER, JSON.stringify({ userId: session.userId, epoch: remote.epoch })); } catch { /* optional storage */ }
    const value = replace ? remote.progress ?? emptyProgress()
      : mergeProgress(this.ports.read(), remote.progress ?? emptyProgress());
    this.ports.write(value, replace);
    if (replace) session.dirty = false;
  }

  private async drain(session: Session): Promise<void> {
    if (!this.live(session) || session.running) return;
    session.running = true;
    this.ports.status('syncing');
    try {
      while (this.live(session)) {
        if (session.pull || session.epoch === undefined) {
          session.pull = false;
          const remote = await pullProgress(session.controller.signal);
          if (!this.live(session)) return;
          if (!remote || remote.userId !== session.userId) throw new Error('Account progress unavailable');
          this.accept(session, remote);
          session.dirty ||= JSON.stringify(this.ports.read()) !== JSON.stringify(remote.progress ?? emptyProgress());
        }
        if (!session.dirty) break;
        session.dirty = false;
        const identity = { userId: session.userId, epoch: session.epoch! };
        const response = await pushProgress(this.ports.read(), identity, session.controller.signal);
        if (!this.live(session)) return;
        if (!response?.snapshot || response.snapshot.userId !== session.userId || ![200, 409].includes(response.status)) {
          session.dirty = true;
          throw new Error('Account progress could not be saved');
        }
        this.accept(session, response.snapshot);
      }
      if (this.live(session)) this.ports.status('synced');
    } catch {
      if (this.live(session)) this.ports.status('error');
    } finally { session.running = false; }
  }
}
